import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { AppSidebar, useFittedSidebarWidth } from "./AppSidebar";
import { Header, APP_HEADER_HEIGHT_PX } from "./Header";
import { useTraccarSession } from "@/hooks/useTraccarSession";
import { FleetAssistantSidebar } from "@/components/chat/FleetAssistantSidebar";
import { CommandResultHandler } from "./CommandResultHandler";
import { WebSocketErrorBanner } from "./WebSocketErrorBanner";

const ASSISTANT_MIN_WIDTH_PX = 480;
// Live Map's panel group is the space left after the sidebar and the vehicle
// list, so the same pixel floor reads much narrower there.
const LIVE_MAP_ASSISTANT_MIN_WIDTH_PX = 720;
const CONTENT_MIN_WIDTH_PX = 280;

interface DashboardLayoutProps {
  children: React.ReactNode;
  headerContent?: React.ReactNode;
  headerActions?: React.ReactNode;
  transparentHeader?: boolean;
  sidebar?: React.ReactNode;
}

export function DashboardLayout({ children, headerContent, headerActions, transparentHeader = false, sidebar }: DashboardLayoutProps) {
  const navigate = useNavigate();
  const { user } = useTraccarSession();
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(APP_HEADER_HEIGHT_PX);
  const fittedSidebarWidth = useFittedSidebarWidth();
  const [sidebarWidth, setSidebarWidth] = useState(fittedSidebarWidth);
  const [allowSidebarTransition, setAllowSidebarTransition] = useState(false);

  useLayoutEffect(() => {
    const frame = requestAnimationFrame(() => setAllowSidebarTransition(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const observer = new ResizeObserver(([entry]) => {
      setHeaderHeight(Math.ceil(entry.target.getBoundingClientRect().height));
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  // Fleet Assistant state
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const panelGroupRef = useRef<HTMLDivElement>(null);
  const [panelGroupWidth, setPanelGroupWidth] = useState(0);

  useLayoutEffect(() => {
    const element = panelGroupRef.current;
    if (!element) return;
    const update = () => setPanelGroupWidth(element.getBoundingClientRect().width);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // A percent default opens the assistant too narrow once the vehicle list
  // takes its share of Live Map. Hold a pixel floor, and on a wide Live Map
  // keep at least 65% of the remaining column so it does not shrink back to a
  // strip. Other pages stay capped at half unless the floor itself needs more.
  const assistantFloorPx = transparentHeader ? LIVE_MAP_ASSISTANT_MIN_WIDTH_PX : ASSISTANT_MIN_WIDTH_PX;
  const shareFloorPercent = transparentHeader ? 65 : 0;
  const desiredMinPercent = panelGroupWidth > 0
    ? Math.max((assistantFloorPx / panelGroupWidth) * 100, shareFloorPercent)
    : transparentHeader ? 65 : 40;
  const mapFloorPercent = panelGroupWidth > 0
    ? (CONTENT_MIN_WIDTH_PX / panelGroupWidth) * 100
    : 30;
  const assistantMinPercent = Math.min(desiredMinPercent, Math.max(25, 100 - mapFloorPercent));
  const preferredMaxPercent = transparentHeader
    ? 100 - mapFloorPercent
    : Math.min(50, 100 - Math.min(mapFloorPercent, 50));
  const assistantMaxPercent = Math.max(assistantMinPercent, preferredMaxPercent);
  const contentMinPercent = Math.max(15, 100 - assistantMaxPercent);
  const assistantDefaultPercent = assistantMinPercent;
  const assistantMinPx = panelGroupWidth > 0
    ? panelGroupWidth * (assistantMinPercent / 100)
    : assistantFloorPx;

  const userInitials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() || 'U';

  const handleLogout = async () => {
    await fetch('/api/session', {method: 'delete'})
    navigate('/login');
  };

  const handleShowHtml = (invocation: { params: { html: string } }) => {
    if (invocation?.params?.html) {
      setHtmlContent(invocation.params.html);
    }
  };

  return (
    <>
    <CommandResultHandler />
    <SidebarProvider
      className={cn(!allowSidebarTransition && "[&>div>div]:!transition-none")}
      style={{ "--sidebar-width": `${sidebarWidth}px` } as React.CSSProperties}
    >
      <AppSidebar headerHeight={headerHeight} onContentWidth={setSidebarWidth} />
      <SidebarInset className="min-w-0 h-svh overflow-hidden flex flex-row">
        {sidebar}
        <div className={`flex flex-col flex-1 min-w-0 ${transparentHeader ? 'relative' : ''}`}>
          <WebSocketErrorBanner />
          <Header
            headerRef={headerRef}
            userInitials={userInitials}
            userEmail={user?.email}
            onLogout={handleLogout}
            headerActions={headerActions}
            onAssistantClick={() => setIsAssistantOpen(!isAssistantOpen)}
            isAssistantOpen={isAssistantOpen}
            transparent={transparentHeader}
          >
            {headerContent}
          </Header>
          <div ref={panelGroupRef} className={transparentHeader ? 'h-full min-w-0' : 'flex-1 min-h-0 min-w-0'}>
          <ResizablePanelGroup direction="horizontal" className="h-full">
            <ResizablePanel defaultSize={isAssistantOpen ? 100 - assistantDefaultPercent : 100} minSize={isAssistantOpen ? contentMinPercent : 50} id="content" order={1}>
              <main className="h-full overflow-y-auto overflow-x-hidden relative">
                {htmlContent && isAssistantOpen ? (
                  <div className="absolute inset-0 bg-white z-10">
                    <iframe
                      srcDoc={htmlContent}
                      className="w-full h-full border-0"
                      title="Generated Content"
                    />
                  </div>
                ) : (
                  children
                )}
              </main>
            </ResizablePanel>
            {isAssistantOpen && (
              <>
                <ResizableHandle withHandle />
                <ResizablePanel
                  defaultSize={assistantDefaultPercent}
                  minSize={assistantMinPercent}
                  maxSize={assistantMaxPercent}
                  id="assistant"
                  order={2}
                  style={{ minWidth: assistantMinPx, flexShrink: 0 }}
                >
                  <div
                    className="flex h-full min-h-0 flex-col bg-background"
                    style={transparentHeader ? { paddingTop: headerHeight } : undefined}
                  >
                    <div className="min-h-0 flex-1 overflow-hidden">
                      <FleetAssistantSidebar
                        isOpen={isAssistantOpen}
                        onClose={() => setIsAssistantOpen(false)}
                        onShowHtml={handleShowHtml}
                        onClearPanel={() => setHtmlContent(null)}
                      />
                    </div>
                  </div>
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
    </>
  );
}
